/**
 * Helpers for the Kai `@` mention tokenizer.
 *
 * A mention stays open across a single space when the current dropdown still
 * has values that contain a space (so the user can type "KING GLORY"). A
 * double space always ends the mention. When no dropdown value contains a
 * space, a single space exits the same way it used to.
 */

/** Find the active `@mention` query that ends at the caret. */
export function detectMentionQuery(textUpToCaret: string): string | null {
  let at = -1;
  for (let i = textUpToCaret.length - 1; i >= 0; i -= 1) {
    if (textUpToCaret[i] === "@") {
      if (i === 0 || /\s/.test(textUpToCaret[i - 1] ?? "")) {
        at = i;
      }
      break;
    }
  }
  if (at < 0) return null;
  const query = textUpToCaret.slice(at + 1);
  // Double space (or more) always ends the mention, even mid-name.
  if (/\s{2}/.test(query)) return null;
  return query;
}

/** True when the current dropdown still has a value the user might type a space into. */
export function dropdownAllowsSpace(labels: string[]): boolean {
  return labels.some((label) => label.includes(" "));
}

/**
 * Whether typing Space should keep the mention open.
 * Double space (query already ends with whitespace) always exits.
 * While results are still loading we keep the mention so a spaced name is not cut off.
 */
export function shouldKeepMentionOnSpace(
  query: string,
  optionLabels: string[],
  loading = false,
): boolean {
  if (/\s$/.test(query)) return false;
  if (loading) return true;
  return dropdownAllowsSpace(optionLabels);
}

/**
 * After a space-exit, stay closed while the user keeps typing after that token.
 * Reopens if they backspace back into the token.
 */
export function mentionStillExited(query: string, exitedQuery: string): boolean {
  if (!exitedQuery) return false;
  if (!query.startsWith(exitedQuery)) return false;
  if (query.length <= exitedQuery.length) return false;
  const next = query[exitedQuery.length];
  return next === " " || next === "\u00a0";
}
